import { marketingContext } from '~/services/marketing.client';
import {defineStore} from 'pinia'
import type {
    changeEmailPayload,
    changePasswordPayload,
    LoginPayload,
    RegisterPayload,
    ResetPayload,
    userInterface
} from "~~/server/types/userInterface";
import type {OrderInterface} from "~~/server/types/OrderInterface";
import {tracking} from "~/services/tracking.client";

const extractErrorMessage = (e: any, fallback: string) => (
    e?.data?.message ||
    e?.response?._data?.message ||
    e?.message ||
    fallback
);

export const useAuthStore = defineStore('auth', {
    state: () => ({
        loading: false as boolean,
        error: null as string | null,

        _user: null as userInterface | null,
        _token: null as string | null,
        _initialized: false as boolean,

        _promise_orders: null as Promise<void> | null,
        _loaded_orders: false,
        _orders: [] as OrderInterface[],
    }),

    getters: {
        token: (state) => state._token,
        haveToken: (state) => !!state._token,
        user: (state) => state._user,
        isAuthenticated: (state) => !!state._user,
        initials:(state)=>state._initialized
            ? `${state._user?.first_name?.[0] ?? ''}`
            : '',
        fullName:(state)=>state._initialized
            ? `${state._user?.first_name ?? ''} ${state._user?.last_name ?? ''}`
            : null,
        initialized: (state) => state._initialized,
        orders: (state) => state._orders,
        loaded_orders: (state) => state._loaded_orders,
        isAdmin: (state) => (state._user?.role || 0) > 0,

    },

    actions: {
        async init() {
            if (this._initialized || this.loading) return

            const { $passportAuth } = useNuxtApp();
            this._token = await $passportAuth.restore(data => {
                this._user = data.user;
                this._initialized = true;
            });

            await this.me()
        },

        setToken(value: string | null, _remember = false) {
            this._token = value;
            this._user = null;
            this._initialized = false;
        },

        async me(force=false): Promise<boolean> {

            if (this._initialized && !force) return !!this._user
            if (!this.token) return false

            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                const res: any = await $api('/auth/me', { method: 'GET' })
                this._user = res as userInterface;
                this._initialized = true
            } catch (e: any) {
                const status = e?.status || e?.response?.status || e?.data?.status
                if (status === 401) this.setToken(null)

                this._user = null
                this._initialized = false
                this.error = extractErrorMessage(e, 'Неуспешно зареждане на профила.')
                return false
            } finally {
                this.loading = false
            }
            return true
        },

        async login(payload: LoginPayload): Promise<boolean> {
            this.error = null;
            this.loading = true;
            try {
                const { $passportAuth } = useNuxtApp();
                const route = useRoute();
                let profile: userInterface | undefined;
                await $passportAuth.login('/auth/login', { ...payload }, route.query.subscription === '1' ? '/profile?tab=subscriptions' : '/', data => { profile = data.user; tracking.login('login-form'); });
                return await this.completeBrowserLogin(profile);
            } catch (error: any) {
                this.error = extractErrorMessage(error, 'Login failed.');
                return false;
            } finally { this.loading = false; }
        },
        async completeSocialLogin(code: string): Promise<boolean> {
            try {
                const { $passportAuth } = useNuxtApp();
                let profile: userInterface | undefined;
                await $passportAuth.login('/auth/social/exchange', { code, remember: true }, '/', data => { profile = data.user; tracking.login('social'); });
                return await this.completeBrowserLogin(profile);
            } catch (error: any) {
                this.error = extractErrorMessage(error, 'Social login failed.');
                return false;
            }
        },
        async register(payload: RegisterPayload): Promise<boolean> {
            this.error = null;
            this.loading = true;
            try {
                const { $passportAuth } = useNuxtApp();
                let profile: userInterface | undefined;
                await $passportAuth.login('/auth/register', { ...payload, remember: true, marketing_tracking: marketingContext() }, '/', data => { profile = data.user; if (data.tracking?.event_id) tracking.registration('register-form', data.tracking.event_id) });
                return await this.completeBrowserLogin(profile);
            } catch (error: any) {
                this.error = extractErrorMessage(error, 'Registration failed.');
                return false;
            } finally { this.loading = false; }
        },

        async completeBrowserLogin(profile?: userInterface): Promise<boolean> {
            const { $passportAuth } = useNuxtApp();
            const cart = useCartStore();
            const favorites = useFavoritesStore();
            this.setToken(await $passportAuth.accessToken());
            if (profile) {
                this._user = profile;
                this._initialized = true;
            } else if (!await this.me(true)) return false;

            favorites.$reset();
            void Promise.allSettled([cart.loadCart(undefined, true), favorites.load()]);
            return true;
        },

        async logout(): Promise<boolean> {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                await useNuxtApp().$passportAuth.logout()
                this.setToken(null);
                await this.me();
            } catch (e: any) {
                this.error = extractErrorMessage(e, 'Logout failed.')
                return false
            } finally {
                this.loading = false
            }
            return true

        },

        async requestPasswordReset(payload: { email: string }): Promise<{ message: string; status: string } | false> {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                return await $api('/auth/password/forgot', {
                    method: 'POST',
                    body: payload,
                })
            } catch (e: any) {
                this.error = extractErrorMessage(e, 'Unable to send reset link.')
                return false
            } finally {
                this.loading = false
            }
        },

        async resetPassword(payload: ResetPayload): Promise<boolean> {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                await $api('/auth/password/reset', {
                    method: 'POST',
                    body: payload,
                })
                this.setToken(null)
                useNuxtApp().$passportAuth.invalidate()

                return true
            } catch (e: any) {
                this.error = extractErrorMessage(e, 'Unable to reset password.')
                return false
            } finally {
                this.loading = false
            }
        },
        async saveProfile(payload:userInterface) {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                await $api('/user/update-profile', {
                    method: 'POST',
                    body: payload,
                })
                await this.me(true);
            }
            catch (e: any) {
                throw e;
            }
            finally {
                this.loading = false
            }
        },
        async changeEmail(payload:changeEmailPayload) {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                await $api('/user/update-email', {
                    method: 'POST',
                    body: payload,
                })
                await this.me(true);
            }
            catch (e: any) {
                throw e;
            }
            finally {
                this.loading = false
            }
        },
        async changePassword(payload:changePasswordPayload) {
            const { $api } = useNuxtApp()

            this.loading = true
            this.error = null

            try {
                await $api('/user/update-password', {
                    method: 'POST',
                    body: payload,
                })
                this.setToken(null)
                useNuxtApp().$passportAuth.invalidate()
            }
            catch (e: any) {
                throw e;
            }
            finally {
                this.loading = false
            }
        },
        async loadOrders() {
           // if (this._loaded_orders) return
            if (this._promise_orders) return this._promise_orders;
            const { $api } = useNuxtApp()

            this._promise_orders = (async () => {
                const res = await $api<OrderInterface[]>('user/orders', { method: 'GET' })
                this._orders = res ?? []
                this._loaded_orders = true
            })()

            try {
                await this._promise_orders
            }
            finally {
                this._promise_orders = null
            }
        },
    },
})
